import React, { useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { createAgency, updateAgency } from '@/api/agencyApi';
import { Agency } from '@/types/agency';
import { cnpjMask, validaCNPJ } from '@/utils';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface AddModalProps {
    agency: Agency | null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Agency) => void;
    onUpdateData: (newData: Agency) => void;
}

const AddEditAgencyModal = ({ agency, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);

    const isEditing = !!agency;

    const validationSchema = Yup.object({
        name: Yup.string().trim().required('O nome é obrigatório'),
        cnpj: Yup.string()
            .required('CNPJ é obrigatório')
            .test('is-valid-cnpj', 'CNPJ inválido', value => validaCNPJ(value)),
        site: Yup.string().trim().required('O site é obrigatório'),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            id: agency ? agency.id : '',
            name: agency ? agency.name : '',
            cnpj: agency ? agency.cnpj : '',
            site: agency ? agency.site : '',
        },
        validationSchema,
        onSubmit: (values: any) => {
            setLoading(true);
            if (agency) {
                updateAgency(values)
                    .then((res: any) => {
                        toggle();
                        toast.success('Agência atualizada com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createAgency(values)
                    .then((res: any) => {
                        toggle();
                        toast.success('Agência cadastrada com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Dialog open={isOpen} onOpenChange={toggle}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isEditing ? 'Editar' : 'Adicionar'} agência</DialogTitle>
                    <DialogDescription>
                        {isEditing ? 'Edite as informações da agência.' : 'Preencha as informações para cadastrar uma nova agência.'}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                    return false;
                }}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="name">Nome</Label>
                            <Input
                                id="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.name && formik.errors.name ? 'border-destructive' : ''}
                            />
                            {formik.touched.name && formik.errors.name && (
                                <p className="text-sm text-destructive">{formik.errors.name}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="cnpj">CNPJ</Label>
                            <Input
                                id="cnpj"
                                value={cnpjMask(formik.values.cnpj)}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.cnpj && formik.errors.cnpj ? 'border-destructive' : ''}
                            />
                            {formik.touched.cnpj && formik.errors.cnpj && (
                                <p className="text-sm text-destructive">{formik.errors.cnpj}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="site">Site</Label>
                            <Input
                                id="site"
                                placeholder="exemplo.com"
                                value={formik.values.site}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.site && formik.errors.site ? 'border-destructive' : ''}
                            />
                            {formik.touched.site && formik.errors.site && (
                                <p className="text-sm text-destructive">{formik.errors.site}</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={toggle}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {isEditing ? 'Editar' : 'Cadastrar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditAgencyModal;
